import React, { useState } from 'react';
import { Volunteering as VolunteeringType } from '../../types';
import Icon from '../../utils/icons';
import './Volunteering.css';

const orgLogos: Record<string, React.ReactNode> = {
    'Action Lab for Development': <Icon name="globe" size={24} />,
    'Ministry of Youth Affairs': <Icon name="sparkles" size={24} />,
    'United Nations': <Icon name="un" size={24} />,
    'United Nations Volunteers': <Icon name="un" size={24} />,
    'Acquaint': <Icon name="handshake" size={24} />,
    'United Nations Development Programme (UNDP)': <Icon name="un" size={24} />,
    'SORUDEO': <Icon name="leaf" size={24} />,
};

const categoryColors: Record<string, { bg: string; text: string }> = {
    'Science and Technology': { bg: '#0369a1', text: '#fff' },
    'Arts and Culture':       { bg: '#7c3aed', text: '#fff' },
    'Human Rights':           { bg: '#dc2626', text: '#fff' },
    'Education':              { bg: '#059669', text: '#fff' },
    'Community':              { bg: '#d97706', text: '#fff' },
};

interface Props {
    volunteering: VolunteeringType[];
}

const Volunteering: React.FC<Props> = ({ volunteering }) => {
    const [expanded, setExpanded] = useState<number | null>(null);

    return (
        <div className="volunteering-wrapper">
            <div className="un-strip">
                <span className="un-strip-icon"><Icon name="un" size={28} /></span>
                <div>
                    <strong>United Nations Online Volunteer</strong>
                    <span>250+ hours contributed across 5 UN & NGO roles</span>
                </div>
                <a
                    href="https://app.unv.org/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="un-strip-link"
                >
                    UNV Platform ↗
                </a>
            </div>

            <div className="vol-grid">
                {volunteering.map((vol, i) => {
                    const catStyle = categoryColors[vol.category] || categoryColors['Community'];
                    const isOpen = expanded === i;
                    return (
                        <div key={i} className={`vol-card ${vol.featured ? 'featured' : ''}`}>
                            <div className="vol-stripe" style={{ background: catStyle.bg }} />

                            <div className="vol-body">
                                <div className="vol-header">
                                    <span className="vol-org-icon">{orgLogos[vol.org] || <Icon name="globe" size={24} />}</span>
                                    <div className="vol-header-text">
                                        <h3 className="vol-role">{vol.role}</h3>
                                        <p className="vol-org">{vol.org}</p>
                                    </div>
                                    {vol.certificate && (
                                        <a
                                            href={vol.certificate}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="cert-badge"
                                            title="View Certificate"
                                        >
                                            <Icon name="certificate" size={20} />
                                        </a>
                                    )}
                                </div>

                                <div className="vol-meta">
                                    <span className="vol-duration">
                                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
                                        </svg>
                                        {vol.duration}
                                    </span>
                                    {vol.hours && (
                                        <span className="vol-hours">{vol.hours}</span>
                                    )}
                                    <span
                                        className="vol-category"
                                        style={{ background: catStyle.bg, color: catStyle.text }}
                                    >
                                        {vol.category}
                                    </span>
                                </div>

                                <p className={`vol-description ${isOpen ? 'open' : ''}`}>
                                    {vol.description}
                                </p>
                                {vol.description && vol.description.length > 120 && (
                                    <button
                                        className="vol-toggle"
                                        onClick={() => setExpanded(isOpen ? null : i)}
                                    >
                                        {isOpen ? 'Show less ▲' : 'Read more ▼'}
                                    </button>
                                )}

                                {(vol.appLink || vol.appLink2 || vol.platformLink) && (
                                    <div className="vol-links">
                                        {vol.appLink && (
                                            <a href={vol.appLink} target="_blank" rel="noopener noreferrer" className="vol-link-btn">
                                                <Icon name="mobile" size={14} /> {vol.appLabel || 'View App'}
                                            </a>
                                        )}
                                        {vol.appLink2 && (
                                            <a href={vol.appLink2} target="_blank" rel="noopener noreferrer" className="vol-link-btn alt">
                                                <Icon name="mobile" size={14} /> {vol.appLabel2 || 'View App'}
                                            </a>
                                        )}
                                        {vol.platformLink && (
                                            <a href={vol.platformLink} target="_blank" rel="noopener noreferrer" className="vol-link-btn outline">
                                                <Icon name="globe" size={14} /> {vol.platformLabel || 'Platform'}
                                            </a>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default React.memo(Volunteering);
